import { FilterProperties } from '@/entities/catalog-old'

// The optimistic client selection JSON — the same shape sent to the products query:
// { CODE: { type, value: [...] }, ... }. Values are stored as FILTERED_VALUE || VALUE.
type SelectionJson = Record<string, { type?: string; value?: (string | number)[] } | undefined>

// Parse the client filter (productsFilterUrl) into a per-code selection, dropping the category
// anchor (LIST_SECTION_CODE) which is not an applied filter. Returns null on empty/invalid input.
export const parseSelectionJson = (filterUrl?: string): SelectionJson | null => {
	if (!filterUrl) return null

	try {
		const parsed = JSON.parse(filterUrl) as SelectionJson
		delete parsed.LIST_SECTION_CODE
		return parsed
	} catch {
		return null
	}
}

// Overlay the user's live selection onto the last-known server properties: recompute each value's
// CHECKED from the selection so applied chips reflect the pick instantly (optimistic), before the
// server filter response for the new URL arrives (sef-url → resolvedUrl → filter refetch). PRICE is
// left untouched — its chip is a range, not a value match, so it stays on the server value to avoid
// regressions. Preserves array/object identity where nothing changed to keep re-renders minimal.
export const overlayAppliedSelection = (properties: FilterProperties[], selection: SelectionJson): FilterProperties[] =>
	properties.map(prop => {
		if (prop.CODE === 'PRICE') return prop

		const selected = (selection[prop.CODE]?.value ?? []).map(String)
		let changed = false

		const values = prop.VALUES.map(value => {
			// Match the stored selection form (FILTERED_VALUE || VALUE — see applied-filters-adapter /
			// useFilter). `||` (not `??`) so an empty/0 FILTERED_VALUE falls back to VALUE like storage does.
			const checked = selected.includes(String(value.FILTERED_VALUE || value.VALUE))
			if (checked === value.CHECKED) return value

			changed = true
			return { ...value, CHECKED: checked }
		})

		return changed ? { ...prop, VALUES: values } : prop
	})
